import { Sparkles, ArrowRight } from "lucide-react"
import { AgentCapabilities } from "./AgentCapabilities"
import { useFtueT } from "./agentFtueEnglish"
import type { PromptCategory } from "@/api/adHocCommands"

// Authenticated dispatchers manage a real fleet; anonymous visitors only get
// the route-planning demo (no driver/fleet context), so the hero copy + primary
// CTA differ. The CTA's prompt is sent to the agent as it is, so it stays out
// of the translation files: Polish, or English when the demo switch is on EN.
const HEROES = {
  fleet: {
    title: "ai.agentWelcome.fleet.title",
    subtitle: "ai.agentWelcome.fleet.subtitle",
    ctaTitle: "ai.agentWelcome.fleet.ctaTitle",
    ctaSub: "ai.agentWelcome.fleet.ctaSub",
    prompt: { pl: "Podsumuj dzisiejszy dzień", en: "Summarize today" },
  },
  anonymous: {
    title: "ai.agentWelcome.anonymous.title",
    subtitle: "ai.agentWelcome.anonymous.subtitle",
    ctaTitle: "ai.agentWelcome.anonymous.ctaTitle",
    ctaSub: "ai.agentWelcome.anonymous.ctaSub",
    prompt: {
      pl: "Wylicz trasę ciężarówki z Warszawy do Berlina",
      en: "Plan a truck route from Warsaw to Berlin",
    },
  },
} as const

type Props = {
  popular: string[]
  groups: PromptCategory[]
  onSelectPrompt: (text: string) => void
  /** Anonymous visitors get generic route-planning copy (no fleet context). */
  isAnonymous?: boolean
  /** Render the copy in English (english-agent-ftue flag). */
  english?: boolean
  /** The demo switch is on PL: render the copy in Polish whatever the UI language. */
  polish?: boolean
}

export function AgentWelcome({
  popular,
  groups,
  onSelectPrompt,
  isAnonymous = false,
  english = false,
  polish = false,
}: Props) {
  const t = useFtueT(english, polish)
  const hero = isAnonymous ? HEROES.anonymous : HEROES.fleet
  const prompt = hero.prompt[english ? "en" : "pl"]

  return (
    <div className="mx-auto flex w-full max-w-[640px] flex-col px-4 py-8">
      <div className="flex flex-col items-center text-center">
        <img src="/slickAvatar.svg" alt="Slick AI" className="mb-4 h-12 w-12" />
        <h1 className="mb-1.5 text-xl font-semibold tracking-tight">
          {t(hero.title)}
        </h1>
        <p className="max-w-[520px] text-sm text-muted-foreground">
          {t(hero.subtitle)}
        </p>
      </div>

      <button
        type="button"
        onClick={() => onSelectPrompt(prompt)}
        className="mt-5 flex items-center gap-3 rounded-xl border border-primary/30 bg-primary/5 px-4 py-3.5 text-left transition-all hover:-translate-y-px hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
      >
        <Sparkles className="h-5 w-5 shrink-0 text-yellow-400 dark:text-yellow-300" />
        <span className="flex-1">
          <span className="block font-semibold">{t(hero.ctaTitle)}</span>
          <span className="block text-[13px] text-muted-foreground">
            {t(hero.ctaSub)}
          </span>
        </span>
        <ArrowRight className="h-4 w-4 shrink-0 text-primary" />
      </button>

      <AgentCapabilities
        className="mt-6"
        popular={popular}
        groups={groups}
        onSelectPrompt={onSelectPrompt}
        english={english}
        polish={polish}
      />
    </div>
  )
}
